import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Star, Paperclip, Archive, Trash2, MailOpen, RefreshCw, SlidersHorizontal,
  Check, Loader2, Inbox, ArrowUpDown, Search, Tag,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../../services/api';
import { getImapFolder, getFolderTitle } from '../../lib/folders';
import { getInitials, getAvatarColor } from '../../lib/avatar';
import { isLabelFolder, labelIdOfFolder, labelsOfKeywords, withLabelChange } from '../../lib/labels';
import { LabelMenu, LabelChips } from '../labels/LabelMenu';

const DAY_MS = 86400000;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

// Bugünden kaç takvim günü önce (gelecek tarihli mailler "bugün" sayılır)
const dayDiff = (dateString) => {
  if (!dateString) return Infinity;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return Infinity;
  return Math.max(0, Math.round((startOfDay(new Date()) - startOfDay(date)) / DAY_MS));
};

const groupLabel = (dateString) => {
  const diff = dayDiff(dateString);
  if (diff === 0) return 'Bugün';
  if (diff === 1) return 'Dün';
  if (diff < 7) return 'Bu hafta';
  if (diff < 31) return 'Bu ay';
  return 'Daha eski';
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const diff = dayDiff(dateString);
  if (diff === 0) return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  if (diff < 7) return date.toLocaleDateString('tr-TR', { weekday: 'short' });
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
};

const readPref = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const FILTERS = [
  { id: 'all', label: 'Tümü' },
  { id: 'unread', label: 'Okunmamış' },
  { id: 'starred', label: 'Yıldızlı' },
  { id: 'attachment', label: 'Eki olan' },
];

export const MailList = ({
  currentFolder,
  selectedMailId,
  onSelectMail,
  isMobile = false,
  searchQuery = '',
  unreadCount = null,
  reloadToken = 0,
  onEmailsChange,
  onMailsRemoved,
  labels = [],
  onManageLabels,
}) => {
  const [emails, setEmails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [newestFirst, setNewestFirst] = useState(true);
  const [labelFilter, setLabelFilter] = useState(null);
  const [showPreview, setShowPreview] = useState(() => readPref('mail.preview', true));
  const [conversationView, setConversationView] = useState(() => readPref('mail.conversations', true));
  const [debouncedQuery, setDebouncedQuery] = useState(searchQuery);
  const requestRef = useRef(0);

  const imapFolder = getImapFolder(currentFolder);
  // Etiket görünümü (sol menüden) ya da bu klasörde etikete göre süzme
  const activeLabelId = isLabelFolder(currentFolder) ? labelIdOfFolder(currentFolder) : labelFilter;
  const activeLabel = labels.find((l) => l.id === activeLabelId) || null;

  const togglePreview = () => {
    setShowPreview((v) => {
      try { localStorage.setItem('mail.preview', JSON.stringify(!v)); } catch { /* tercih saklanamadı */ }
      return !v;
    });
  };

  const toggleConversations = () => {
    setConversationView((v) => {
      try { localStorage.setItem('mail.conversations', JSON.stringify(!v)); } catch { /* tercih saklanamadı */ }
      return !v;
    });
  };

  // Arama kutusu yazıldıkça her tuşta IMAP'e gitme
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Eski bir isteğin cevabı yenisinin üstüne yazmasın diye istek sayacı kullanılır
  const fetchEmails = useCallback(async (silent = false) => {
    const requestId = ++requestRef.current;
    if (!silent) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const response = await api.getMessages(imapFolder, 1, 50, debouncedQuery || null, activeLabelId);
      if (requestId !== requestRef.current) return;
      setEmails(response.messages || []);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      if (!silent) {
        console.error('Failed to load emails:', err);
        setError(err.message || 'Mail sunucusuna bağlanılamadı');
        setEmails([]);
      }
    } finally {
      if (!silent && requestId === requestRef.current) setIsLoading(false);
    }
  }, [imapFolder, debouncedQuery, activeLabelId]);

  // Klasör değişince listeyi sıfırla
  useEffect(() => {
    setEmails([]);
    setSelectedIds([]);
    setActiveFilter('all');
    setLabelFilter(null);
  }, [currentFolder]);

  // Klasör / arama / dışarıdan yenileme isteği değişince yükle
  useEffect(() => {
    fetchEmails(false);
  }, [fetchEmails, reloadToken]);

  // Arka planda 10 sn'de bir sessiz yenileme
  useEffect(() => {
    const id = setInterval(() => fetchEmails(true), 10000);
    return () => clearInterval(id);
  }, [fetchEmails]);

  const filteredEmails = useMemo(() => {
    const filtered = emails.filter((email) => {
      if (currentFolder === 'starred' && !email.is_starred) return false;
      switch (activeFilter) {
        case 'unread': return !email.is_read;
        case 'starred': return email.is_starred;
        case 'attachment': return email.has_attachment;
        default: return true;
      }
    });
    const time = (e) => (e.date ? new Date(e.date).getTime() : 0);
    return [...filtered].sort((a, b) => (newestFirst ? time(b) - time(a) : time(a) - time(b)));
  }, [emails, currentFolder, activeFilter, newestFirst]);

  // Konuşma görünümü: aynı konudaki (References zinciri) mailler tek satırda toplanır; satırı en yeni mail temsil eder
  const visibleEmails = useMemo(() => {
    const solo = (e) => ({ ...e, count: 1, memberIds: [e.id] });
    if (!conversationView || currentFolder === 'drafts') return filteredEmails.map(solo);
    const time = (e) => (e.date ? new Date(e.date).getTime() : 0);
    const byKey = new Map();
    filteredEmails.forEach((e) => {
      const key = e.thread_key || `id:${e.id}`;
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(e);
    });
    const rows = [...byKey.values()].map((members) => {
      const rep = members.reduce((a, b) => (time(b) > time(a) ? b : a));
      if (members.length === 1) return solo(rep);
      const keywords = [...new Set(members.flatMap((m) => m.keywords || []))];
      return {
        ...rep,
        count: members.length,
        memberIds: members.map((m) => m.id),
        is_read: members.every((m) => m.is_read),
        has_attachment: members.some((m) => m.has_attachment),
        keywords,
      };
    });
    return rows.sort((a, b) => (newestFirst ? time(b) - time(a) : time(a) - time(b)));
  }, [filteredEmails, conversationView, currentFolder, newestFirst]);

  // Satır -> o konuşmadaki tüm mail UID'leri (toplu işlemler konuşmanın tamamına uygulanır)
  const expandIds = useCallback((ids) => {
    const rowById = new Map(visibleEmails.map((r) => [r.id, r]));
    return [...new Set(ids.flatMap((id) => rowById.get(id)?.memberIds || [id]))];
  }, [visibleEmails]);

  // Dashboard'a görünen id'leri bildir (önceki/sonraki mail gezinmesi için)
  useEffect(() => {
    onEmailsChange?.(visibleEmails.map((e) => e.id));
  }, [visibleEmails, onEmailsChange]);

  // Ardışık aynı etiketli mailleri "Bugün / Dün / ..." başlıkları altında grupla
  const groups = useMemo(() => {
    const result = [];
    visibleEmails.forEach((email) => {
      const label = groupLabel(email.date);
      const last = result[result.length - 1];
      if (last && last.label === label) last.items.push(email);
      else result.push({ label, items: [email] });
    });
    return result;
  }, [visibleEmails]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchEmails(false);
    setIsRefreshing(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const allSelected = visibleEmails.length > 0 && selectedIds.length === visibleEmails.length;
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : visibleEmails.map((e) => e.id));
  };

  const handleToggleStar = async (e, emailId, isStarred) => {
    e.stopPropagation();
    try {
      await api.toggleStar(emailId, !isStarred, imapFolder);
      setEmails((prev) => prev.map((m) => (m.id === emailId ? { ...m, is_starred: !isStarred } : m)));
    } catch (err) {
      toast.error('İşlem başarısız', { description: err.message || 'Bir hata oluştu' });
    }
  };

  // Seçili mailler için etiketin durumu: true (hepsinde) | 'mixed' | false
  const labelState = (labelId) => {
    const expanded = expandIds(selectedIds);
    const chosen = emails.filter((m) => expanded.includes(m.id));
    const withLabel = chosen.filter((m) => labelsOfKeywords(m.keywords, labels).some((l) => l.id === labelId)).length;
    if (chosen.length > 0 && withLabel === chosen.length) return true;
    return withLabel > 0 ? 'mixed' : false;
  };

  const handleToggleLabel = async (label, currentlyAll) => {
    const ids = expandIds(selectedIds);
    const add = currentlyAll ? [] : [label.id];
    const remove = currentlyAll ? [label.id] : [];
    const previous = emails;
    setEmails((prev) => prev.map((m) => (ids.includes(m.id) ? { ...m, keywords: withLabelChange(m.keywords, add, remove) } : m)));
    try {
      await api.applyLabels(imapFolder, ids, add, remove);
      if (activeLabelId && remove.includes(activeLabelId)) fetchEmails(true); // etiket görünümünden çıkanlar listeden kalksın
    } catch (err) {
      setEmails(previous);
      toast.error('Etiket uygulanamadı', { description: err.message || 'Bir hata oluştu' });
    }
  };

  const handleMarkAsRead = async () => {
    if (selectedIds.length === 0) return;
    const ids = expandIds(selectedIds);
    try {
      for (const msgId of ids) {
        await api.markAsRead(msgId, imapFolder);
      }
      setEmails((prev) => prev.map((m) => (ids.includes(m.id) ? { ...m, is_read: true } : m)));
      toast.success(`${ids.length} mail okundu olarak işaretlendi`, { duration: 2000 });
      setSelectedIds([]);
    } catch (err) {
      toast.error('İşlem başarısız', { description: err.message || 'Bir hata oluştu' });
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    const ids = expandIds(selectedIds);
    if (!window.confirm(`${ids.length} mail silinecek. Emin misiniz?`)) return;
    try {
      for (const msgId of ids) {
        await api.deleteMessage(msgId, imapFolder);
      }
      setSelectedIds([]);
      onMailsRemoved?.(ids);
      await fetchEmails(false);
      toast.success(`${ids.length} mail silindi`, { duration: 3000 });
    } catch (err) {
      toast.error('Mail silinemedi', { description: err.message || 'Bir hata oluştu' });
    }
  };

  const handleArchiveSelected = async () => {
    if (selectedIds.length === 0) return;
    const ids = expandIds(selectedIds);
    try {
      for (const msgId of ids) {
        await api.moveMessage(msgId, imapFolder, getImapFolder('archive'));
      }
      setSelectedIds([]);
      onMailsRemoved?.(ids);
      await fetchEmails(false);
      toast.success(`${ids.length} mail arşivlendi`, { duration: 3000 });
    } catch (err) {
      toast.error('Arşivlenemedi', { description: err.message || 'Bir hata oluştu' });
    }
  };

  const widthClass = isMobile ? 'w-full' : 'w-[380px] xl:w-[420px]';
  const subtitle = debouncedQuery
    ? `"${debouncedQuery}" için sonuçlar`
    : currentFolder === 'inbox' && unreadCount !== null
      ? `${unreadCount} okunmamış`
      : `${visibleEmails.length} mail`;

  return (
    <div
      className={`flex flex-col h-full bg-background border-r border-border overflow-hidden shrink-0 ${widthClass}`}
      data-testid="mail-list"
    >
      {/* Başlık */}
      <div className="px-5 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold truncate" data-testid="folder-title">
              {isLabelFolder(currentFolder) ? (activeLabel?.name || 'Etiket') : getFolderTitle(currentFolder)}
            </h2>
            <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              aria-label="Yenile"
              className="h-9 w-9 rounded-lg"
              data-testid="refresh-button"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label="Filtrele"
                  className={`h-9 w-9 rounded-lg ${activeFilter === 'starred' || activeFilter === 'attachment' ? 'border-primary text-primary' : ''}`}
                  data-testid="filter-button"
                >
                  <SlidersHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {FILTERS.map((f) => (
                  <DropdownMenuItem key={f.id} onClick={() => setActiveFilter(f.id)}>
                    <Check className={`h-4 w-4 mr-2 ${activeFilter === f.id ? 'opacity-100' : 'opacity-0'}`} />
                    {f.label}
                  </DropdownMenuItem>
                ))}
                {!isLabelFolder(currentFolder) && labels.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">Etikete göre</DropdownMenuLabel>
                    {labels.map((l) => (
                      <DropdownMenuItem key={l.id} onClick={() => setLabelFilter(labelFilter === l.id ? null : l.id)} data-testid={`filter-label-${l.id}`}>
                        <Check className={`h-4 w-4 mr-2 ${labelFilter === l.id ? 'opacity-100' : 'opacity-0'}`} />
                        {l.name}
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={toggleConversations} data-testid="toggle-conversations">
                  <Check className={`h-4 w-4 mr-2 ${conversationView ? 'opacity-100' : 'opacity-0'}`} />
                  Konuşma görünümü
                </DropdownMenuItem>
                <DropdownMenuItem onClick={togglePreview} data-testid="toggle-preview">
                  <Check className={`h-4 w-4 mr-2 ${showPreview ? 'opacity-100' : 'opacity-0'}`} />
                  Önizleme metni
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Sekmeler */}
        <div className="flex gap-6 mt-3 -mb-px">
          {FILTERS.slice(0, 2).map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`pb-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeFilter === f.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${f.id}`}
            >
              {f.label}
            </button>
          ))}
          {labelFilter && activeLabel && !isLabelFolder(currentFolder) && (
            <button onClick={() => setLabelFilter(null)} className="pb-2.5 text-sm font-medium border-b-2 border-primary text-primary flex items-center gap-1.5" title="Süzmeyi kaldır" data-testid="active-label-filter">
              <Tag className="h-3.5 w-3.5" />{activeLabel.name}
            </button>
          )}
          {(activeFilter === 'starred' || activeFilter === 'attachment') && (
            <span className="pb-2.5 text-sm font-medium border-b-2 border-primary text-primary">
              {FILTERS.find((f) => f.id === activeFilter).label}
            </span>
          )}
        </div>
      </div>

      {/* Seçim / sıralama çubuğu */}
      <div className="h-11 px-5 flex items-center justify-between border-y border-border bg-panel/60">
        {selectedIds.length > 0 ? (
          <div className="flex items-center gap-1 -ml-1.5">
            <button
              onClick={toggleSelectAll}
              className="px-1.5 text-sm font-medium text-primary"
              aria-label="Tümünü seç / bırak"
            >
              {selectedIds.length} seçili
            </button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleArchiveSelected} aria-label="Arşivle" data-testid="archive-selected">
              <Archive className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={handleDeleteSelected} aria-label="Sil" data-testid="delete-selected">
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleMarkAsRead} aria-label="Okundu işaretle" data-testid="mark-read-selected">
              <MailOpen className="h-4 w-4" />
            </Button>
            <LabelMenu
              labels={labels}
              checked={labelState}
              onToggle={handleToggleLabel}
              onManage={onManageLabels}
              buttonClass="h-8 w-8 p-0"
              iconOnly
              testId="label-selected"
            />
          </div>
        ) : (
          <button
            onClick={toggleSelectAll}
            aria-label="Tümünü seç"
            aria-pressed={allSelected}
            className={`h-[18px] w-[18px] rounded border flex items-center justify-center transition-colors ${
              allSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/50 hover:border-primary'
            }`}
          >
            {allSelected && <Check className="h-3 w-3" />}
          </button>
        )}
        <button
          onClick={() => setNewestFirst((v) => !v)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          data-testid="sort-toggle"
        >
          <ArrowUpDown className="h-3.5 w-3.5" />
          {newestFirst ? 'Yeniden eskiye' : 'Eskiden yeniye'}
        </button>
      </div>

      {/* Liste */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin">
        {isLoading && emails.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
              <Inbox className="h-7 w-7 text-destructive" />
            </div>
            <h3 className="font-medium mb-1">Bağlantı hatası</h3>
            <p className="text-sm text-muted-foreground mb-4">{error}</p>
            <Button onClick={handleRefresh} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Tekrar dene
            </Button>
          </div>
        ) : visibleEmails.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-3">
              {debouncedQuery ? <Search className="h-6 w-6 text-muted-foreground" /> : <MailOpen className="h-6 w-6 text-muted-foreground" />}
            </div>
            <p className="text-sm text-muted-foreground">
              {debouncedQuery ? 'Aramanızla eşleşen mail yok' : activeLabelId ? 'Bu etiketle işaretli mail yok' : 'Bu klasörde mail yok'}
            </p>
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.label}>
              <h3 className="px-5 pt-4 pb-2 text-xs font-semibold text-muted-foreground">{group.label}</h3>
              {group.items.map((email) => {
                const isSelected = selectedMailId === email.id;
                const isChecked = selectedIds.includes(email.id);
                const senderName = email.from_addr?.name || email.from_addr?.email || 'Bilinmeyen';
                return (
                  <div
                    key={email.id}
                    onClick={() => onSelectMail(email.id)}
                    className={`group relative flex items-start gap-3 pl-5 pr-4 py-3 cursor-pointer border-b border-border/60 transition-colors ${
                      isSelected ? 'bg-accent' : 'hover:bg-secondary/70'
                    }`}
                    data-testid={`mail-item-${email.id}`}
                  >
                    {!email.is_read && (
                      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-primary" aria-label="Okunmamış" />
                    )}

                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(email.id); }}
                      aria-label="Seç"
                      className={`h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                        isChecked
                          ? 'bg-primary text-primary-foreground'
                          : getAvatarColor(email.from_addr?.email || senderName)
                      }`}
                    >
                      {isChecked ? <Check className="h-5 w-5" /> : getInitials(email.from_addr?.name, email.from_addr?.email)}
                    </button>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm truncate ${!email.is_read ? 'font-semibold' : 'font-medium'}`}>
                          {senderName}
                          {email.count > 1 && <span className="ml-1.5 text-xs font-normal text-muted-foreground" data-testid={`thread-count-${email.id}`}>({email.count})</span>}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0 whitespace-nowrap">
                          {formatTime(email.date)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className={`flex-1 text-sm truncate ${!email.is_read ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                          {email.subject || '(Konu yok)'}
                        </p>
                        {email.has_attachment && <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                        <button
                          onClick={(e) => handleToggleStar(e, email.id, email.is_starred)}
                          aria-label={email.is_starred ? 'Yıldızı kaldır' : 'Yıldızla'}
                          className={`shrink-0 transition-opacity ${email.is_starred ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
                          data-testid={`star-${email.id}`}
                        >
                          <Star className={`h-4 w-4 ${email.is_starred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground hover:text-yellow-400'}`} />
                        </button>
                      </div>
                      <LabelChips labels={labelsOfKeywords(email.keywords, labels)} className="mt-1" />
                      {showPreview && email.preview && (
                        <p className="mt-0.5 text-xs text-muted-foreground/90 truncate" data-testid={`preview-${email.id}`}>{email.preview}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
          ))
        )}
      </div>
    </div>
  );
};

export default MailList;
